// The first bytes the backend expects for each picture type (FileSignatureValidator); it compares them with the type
// the browser gives the file from its extension.
const PICTURE_SIGNATURES: Record<string, number[][]> = {
	"image/jpeg": [[0xff, 0xd8, 0xff]],
	"image/jpg": [[0xff, 0xd8, 0xff]],
	"image/png": [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
	"image/gif": [
		[0x47, 0x49, 0x46, 0x38, 0x37, 0x61],
		[0x47, 0x49, 0x46, 0x38, 0x39, 0x61],
	],
};
const SIGNATURE_LENGTH = 8;

const readHeader = (file: File): Promise<Uint8Array> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(file.slice(0, SIGNATURE_LENGTH));
	});

// A PNG renamed to .jpg, for example, has the wrong first bytes for its type and is refused by the backend.
export const hasPictureSignature = async (file: File): Promise<boolean> => {
	const signatures = PICTURE_SIGNATURES[file.type] ?? [];
	const header = await readHeader(file);
	return signatures.some(signature => signature.every((byte, index) => header[index] === byte));
};

// A broken file can still start with the right bytes; the browser has to be able to draw it.
const isPictureDecodable = (file: File): Promise<boolean> =>
	new Promise(resolve => {
		const image = new Image();
		const url = URL.createObjectURL(file);
		const finish = (decodable: boolean) => {
			URL.revokeObjectURL(url);
			resolve(decodable);
		};
		image.onload = () => finish(image.naturalWidth > 0);
		image.onerror = () => finish(false);
		image.src = url;
	});

// No picture is valid too: the picture is optional.
export const isPictureValid = async (file?: File | null): Promise<boolean> =>
	!file || ((await hasPictureSignature(file)) && (await isPictureDecodable(file)));
